import type { LandingCampaignResponse } from '@/types/landing'
import { ApiClientError } from '@/lib/api/client'
import {
  getLandingApiBaseUrls,
  isLandingApiProxyMiss,
  rememberLandingApiProxyMiss,
} from '@/lib/landing-api-bases'

/**
 * Busca dados da landing page de uma campanha pelo slug.
 * Endpoint público, não requer autenticação.
 *
 * @param slug - Slug da campanha (identificador amigável na URL)
 * @returns Dados completos da landing page
 * @throws ApiClientError se a campanha não for encontrada (404) ou houver erro de rede
 */
export async function fetchLandingCampaign(slug: string): Promise<LandingCampaignResponse> {
  const bases = getLandingApiBaseUrls()
  let lastError: unknown = null

  for (const baseUrl of bases) {
    const url = `${baseUrl}/api/public/campaigns/${encodeURIComponent(slug)}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (isLandingApiProxyMiss(response)) {
        rememberLandingApiProxyMiss()
        lastError = new ApiClientError('Não foi possível carregar a campanha.', response.status)
        continue
      }

      const data: LandingCampaignResponse = await response.json()

      if (response.status === 404) {
        throw new ApiClientError('Campanha não encontrada', 404)
      }

      if (!response.ok) {
        throw new ApiClientError('Erro ao carregar campanha', response.status)
      }

      if (data.success === false) {
        const errorMessage =
          (data as unknown as { message?: string }).message || 'Campanha temporariamente indisponível'
        throw new ApiClientError(errorMessage, response.status)
      }

      return data
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404) {
        throw error
      }
      lastError = error
    }
  }

  if (lastError instanceof ApiClientError) {
    throw lastError
  }
  if (lastError instanceof TypeError && lastError.message.includes('fetch')) {
    throw new ApiClientError('Erro de conexão. Verifique sua internet e tente novamente.', 0)
  }
  throw new ApiClientError(
    lastError instanceof Error ? lastError.message : 'Erro ao carregar campanha',
    0
  )
}

export async function fetchCampaignSlugByHost(
  host: string,
  signal?: AbortSignal
): Promise<string | null> {
  const params = new URLSearchParams({ host })
  const bases = getLandingApiBaseUrls()

  for (const baseUrl of bases) {
    const url = `${baseUrl}/api/public/campaigns/by-host?${params.toString()}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal,
      })

      if (response.status === 404 || response.status === 400 || response.status === 422) {
        return null
      }

      if (isLandingApiProxyMiss(response)) {
        rememberLandingApiProxyMiss()
        continue
      }

      if (!response.ok) {
        continue
      }

      const payload = (await response.json()) as {
        success?: boolean
        data?: { slug?: string }
      }

      const slug = payload.data?.slug?.trim() ?? ''
      if (payload.success === false || slug === '') {
        return null
      }

      return slug
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
    }
  }

  return null
}
